<template>
  <div
    v-if="show"
    class="upload-modal-overlay"
    @click.self="handleOverlayClick"
  >
    <div class="upload-modal">
      <div class="upload-modal-header">
        <h3>{{ modalTitle }}</h3>
        <button class="close-btn" @click="closeModal" :disabled="uploading && !uploadSuccess">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="upload-modal-body">
        <!-- Success State -->
        <div v-if="uploadSuccess" class="upload-success-state">
          <div class="success-icon">
            <i class="bi bi-check-circle-fill"></i>
          </div>
          <div class="success-text">{{ successMessage }}</div>
          <button
            v-if="uploadedMaterialId"
            class="btn btn-outline-primary mt-3"
            @click="goToMaterial"
          >
            <i class="bi bi-arrow-right me-2"></i>
            {{ $t('upload.go_to_material') }}
          </button>
        </div>

        <!-- Upload State (not success) -->
        <template v-else>
          <!-- Drop Zone -->
          <div
            class="drop-zone"
            :class="{
              'drop-zone-active': isDragging,
              'drop-zone-disabled': uploading,
              'drop-zone-has-file': selectedFile && !uploading
            }"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
          >
            <input
              ref="fileInput"
              type="file"
              accept="audio/*,video/*"
              @change="handleFileSelect"
              style="display: none"
            />

            <template v-if="!selectedFile && !uploading">
              <div class="drop-zone-icon">
                <i class="bi bi-cloud-arrow-up"></i>
              </div>
              <div class="drop-zone-text">
                <span class="drop-zone-main">{{ $t('upload.drop_here') }}</span>
                <span class="drop-zone-hint">{{ $t('upload.or_click') }}</span>
              </div>
              <div class="drop-zone-formats">
                {{ $t('upload.supported_formats') }}
              </div>
            </template>

            <template v-else-if="selectedFile && !uploading">
              <div class="selected-file">
                <div class="file-icon">
                  <i :class="fileIcon"></i>
                </div>
                <div class="file-info">
                  <div class="file-name">{{ selectedFile.name }}</div>
                  <div class="file-size">{{ formatFileSize(selectedFile.size) }}</div>
                </div>
                <button class="remove-file-btn" @click.stop="removeFile">
                  <i class="bi bi-x-circle"></i>
                </button>
              </div>
            </template>

            <template v-else>
              <div class="upload-progress">
                <div class="progress-spinner">
                  <SpinnerComponent :visible="true" />
                </div>
                <div class="progress-text">{{ $t('upload.uploading') }}</div>
                <div class="progress-bar-container">
                  <div class="progress-bar" :style="{ width: uploadProgress + '%' }"></div>
                </div>
                <div class="progress-percent">{{ uploadProgress }}%</div>
              </div>
            </template>
          </div>

          <!-- File Title -->
          <div class="form-group" v-if="selectedFile && !uploading">
            <label for="fileTitle">{{ $t('upload.title_label') }}</label>
            <input
              id="fileTitle"
              v-model="fileTitle"
              type="text"
              class="form-control"
              :placeholder="$t('upload.title_placeholder')"
            />
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="error-message">
            <i class="bi bi-exclamation-triangle"></i>
            {{ errorMessage }}
          </div>
        </template>
      </div>

      <div class="upload-modal-footer">
        <!-- Success: only Close button -->
        <template v-if="uploadSuccess">
          <button class="btn btn-primary" @click="handleSuccessClose">
            <i class="bi bi-check-lg me-2"></i>
            {{ $t('common.close') }}
          </button>
        </template>
        <!-- Normal: Cancel + Upload -->
        <template v-else>
          <button
            class="btn btn-secondary"
            @click="closeModal"
            :disabled="uploading"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            class="btn btn-primary"
            @click="uploadFile"
            :disabled="!canUpload"
          >
            <i class="bi bi-cloud-upload me-2"></i>
            {{ $t('upload.button') }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import SpinnerComponent from '@/components/SpinnerComponent.vue';
import axiosConfig from '@/lib/axiosConfig';
import { API_URL } from '@/config.js';
import { mapGetters } from 'vuex';

export default {
  name: 'UploadFileModal',

  components: {
    SpinnerComponent
  },

  props: {
    show: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close', 'uploaded'],

  data() {
    return {
      isDragging: false,
      selectedFile: null,
      fileTitle: '',
      uploading: false,
      uploadProgress: 0,
      errorMessage: '',
      successMessage: '',
      uploadSuccess: false,
      uploadedMaterialId: null,
      maxFileSize: 500 * 1024 * 1024 // 500MB
    };
  },

  computed: {
    ...mapGetters('app', ['configData']),

    serviceName() {
      return this.configData?.service_name || this.configData?.chatbot_name || 'vid2text';
    },

    modalTitle() {
      if (this.serviceName === 'clipcut' || this.serviceName === 'narezkavideo') {
        return this.$t('upload.title_clipcut');
      } else if (this.serviceName === 'vid2text') {
        return this.$t('upload.title_vid2text');
      }
      return this.$t('upload.title');
    },

    canUpload() {
      return this.selectedFile && !this.uploading && !this.successMessage;
    },

    fileIcon() {
      if (!this.selectedFile) return 'bi bi-file-earmark';

      const type = this.selectedFile.type;
      if (type.startsWith('video/')) return 'bi bi-file-earmark-play';
      if (type.startsWith('audio/')) return 'bi bi-file-earmark-music';
      return 'bi bi-file-earmark';
    }
  },

  watch: {
    show(val) {
      if (val) {
        this.resetState();
      }
    }
  },

  methods: {
    resetState() {
      this.selectedFile = null;
      this.fileTitle = '';
      this.uploading = false;
      this.uploadProgress = 0;
      this.errorMessage = '';
      this.successMessage = '';
      this.uploadSuccess = false;
      this.uploadedMaterialId = null;
      this.isDragging = false;
    },

    closeModal() {
      if (!this.uploading || this.uploadSuccess) {
        this.$emit('close');
      }
    },

    handleOverlayClick() {
      if (!this.uploading || this.uploadSuccess) {
        this.closeModal();
      }
    },

    handleSuccessClose() {
      this.$emit('uploaded', { materialId: this.uploadedMaterialId });
      this.$emit('close');
    },

    goToMaterial() {
      if (this.uploadedMaterialId) {
        this.$router.push(`/app/article/${this.uploadedMaterialId}`);
        this.$emit('close');
      }
    },

    triggerFileInput() {
      if (!this.uploading) {
        this.$refs.fileInput.click();
      }
    },

    handleDragOver() {
      if (!this.uploading) {
        this.isDragging = true;
      }
    },

    handleDragLeave() {
      this.isDragging = false;
    },

    handleDrop(e) {
      this.isDragging = false;
      if (this.uploading) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    handleFileSelect(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    processFile(file) {
      this.errorMessage = '';

      // Validate file type
      if (!file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
        this.errorMessage = this.$t('upload.error_invalid_type');
        return;
      }

      // Validate file size
      if (file.size > this.maxFileSize) {
        this.errorMessage = this.$t('upload.error_too_large', { size: '500MB' });
        return;
      }

      this.selectedFile = file;
      // Auto-fill title from filename (without extension)
      this.fileTitle = file.name.replace(/\.[^/.]+$/, '');
    },

    removeFile() {
      this.selectedFile = null;
      this.fileTitle = '';
      this.errorMessage = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async uploadFile() {
      if (!this.selectedFile) return;

      this.uploading = true;
      this.uploadProgress = 0;
      this.errorMessage = '';

      const formData = new FormData();
      formData.append('action', 'upload_file');
      formData.append('file', this.selectedFile);
      formData.append('title', this.fileTitle || this.selectedFile.name);

      try {
        const response = await axiosConfig.post(API_URL, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          }
        });

        const data = response.data;

        if (data.success) {
          // Set success message based on service
          if (this.serviceName === 'clipcut' || this.serviceName === 'narezkavideo') {
            this.successMessage = this.$t('upload.success_clipcut');
          } else if (this.serviceName === 'vid2text') {
            this.successMessage = this.$t('upload.success_vid2text');
          } else {
            this.successMessage = this.$t('upload.success');
          }

          this.uploadProgress = 100;
          this.uploadSuccess = true;
          this.uploading = false;
          this.uploadedMaterialId = data.queue_id || data.material_id;
        } else {
          this.errorMessage = data.error || this.$t('upload.error_generic');
          this.uploading = false;
        }
      } catch (error) {
        console.error('Upload error:', error);
        this.errorMessage = this.$t('upload.error_generic');
        this.uploading = false;
      }
    }
  }
};
</script>

<style scoped>
.upload-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.upload-modal {
  background: var(--bs-body-bg);
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.upload-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--bs-border-color);
}

.upload-modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--bs-emphasis-color);
}

.close-btn {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--bs-secondary-color);
  border-radius: 8px;
  transition: all 0.2s;
}

.close-btn:hover:not(:disabled) {
  background: var(--bs-tertiary-bg);
  color: var(--bs-emphasis-color);
}

.close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.upload-modal-body {
  padding: 1.5rem;
}

/* Success State */
.upload-success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
}

.success-icon {
  font-size: 4rem;
  color: #10b981;
  margin-bottom: 1.5rem;
  animation: successPop 0.4s ease-out;
}

@keyframes successPop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.success-text {
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--bs-emphasis-color);
  max-width: 300px;
}

.drop-zone {
  border: 2px dashed var(--bs-border-color);
  border-radius: 12px;
  padding: 2.5rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: var(--bs-tertiary-bg);
}

.drop-zone:hover:not(.drop-zone-disabled) {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.05);
}

.drop-zone-active {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  transform: scale(1.02);
}

.drop-zone-disabled {
  cursor: default;
  opacity: 0.9;
}

.drop-zone-has-file {
  border-style: solid;
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
}

.drop-zone-icon {
  font-size: 3rem;
  color: #6366f1;
  margin-bottom: 1rem;
}

.drop-zone-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.drop-zone-main {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--bs-emphasis-color);
}

.drop-zone-hint {
  font-size: 0.9rem;
  color: var(--bs-secondary-color);
}

.drop-zone-formats {
  font-size: 0.8rem;
  color: var(--bs-tertiary-color);
}

/* Selected File */
.selected-file {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
}

.file-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.file-info {
  flex: 1;
  text-align: left;
  min-width: 0;
}

.file-name {
  font-weight: 600;
  color: var(--bs-emphasis-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: 0.85rem;
  color: var(--bs-secondary-color);
}

.remove-file-btn {
  background: none;
  border: none;
  color: var(--bs-secondary-color);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s;
}

.remove-file-btn:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

/* Upload Progress */
.upload-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.progress-text {
  font-weight: 500;
  color: var(--bs-emphasis-color);
}

.progress-bar-container {
  width: 100%;
  height: 6px;
  background: var(--bs-border-color);
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-percent {
  font-size: 0.9rem;
  color: var(--bs-secondary-color);
}

/* Form Group */
.form-group {
  margin-top: 1.5rem;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--bs-emphasis-color);
  margin-bottom: 0.5rem;
}

.form-group .form-control {
  border-radius: 10px;
  padding: 0.75rem 1rem;
  border: 1px solid var(--bs-border-color);
  background: var(--bs-body-bg);
  color: var(--bs-body-color);
}

.form-group .form-control:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

/* Messages */
.error-message,
.success-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  margin-top: 1rem;
  font-size: 0.9rem;
}

.error-message {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.success-message {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

/* Footer */
.upload-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid var(--bs-border-color);
}

.upload-modal-footer .btn {
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  font-weight: 500;
}

.upload-modal-footer .btn-primary {
  background: #6366f1;
  border-color: #6366f1;
}

.upload-modal-footer .btn-primary:hover:not(:disabled) {
  background: #4f46e5;
  border-color: #4f46e5;
}

.upload-modal-footer .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Dark Mode */
html[data-bs-theme="dark"] .upload-modal {
  background: var(--yt-bg-secondary, #1f2937);
}

html[data-bs-theme="dark"] .drop-zone {
  background: var(--yt-bg-tertiary, #374151);
  border-color: var(--yt-border-color, #4b5563);
}

html[data-bs-theme="dark"] .drop-zone:hover:not(.drop-zone-disabled) {
  background: rgba(99, 102, 241, 0.1);
}

html[data-bs-theme="dark"] .form-group .form-control {
  background: var(--yt-bg-tertiary, #374151);
  border-color: var(--yt-border-color, #4b5563);
}

/* Responsive */
@media (max-width: 575px) {
  .upload-modal {
    max-width: none;
    margin: 0.5rem;
    border-radius: 12px;
  }

  .drop-zone {
    padding: 2rem 1rem;
  }

  .upload-modal-footer {
    flex-direction: column;
  }

  .upload-modal-footer .btn {
    width: 100%;
  }
}
</style>
